import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useT } from '@/contexts/I18nContext';

import {
  type CategoryTreeFields,
  type CategoryVariant,
  deriveClosingSeverity,
  deriveOpeningProblem,
  serializeCategoryTree,
} from './treeBlock';

const VARIANTS: CategoryVariant[] = ['issues', 'ok', 'no_data'];

/**
 * Edits a whole "Выкладка" category tree block as one unit (build_category_tree
 * in services/.../daily_report/sections/empty_shelf.py). Mirrors
 * KeypointBlockForm's pattern: a flat ok/no_data one-liner OR the branch
 * form (Просадки always; На открытии/К закрытию always; Вышла в норму /
 * Пополнялась в последний раз only when their *_problem flag is set and
 * this isn't the "never reached fill threshold all day" fallback — see
 * treeBlock.ts's `serializeCategoryTree` for the exact branch logic this
 * form's live preview reproduces).
 */
export function CategoryTreeForm({
  initial,
  openTime,
  onApply,
  onCancel,
}: {
  initial: CategoryTreeFields;
  openTime: string | null;
  onApply: (rows: string[]) => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<CategoryTreeFields>(initial);
  const set = (patch: Partial<CategoryTreeFields>) => setF((prev) => ({ ...prev, ...patch }));

  // Gate checkbox for the whole "opening" subsection (Alexey 2026-09-04:
  // "низкая заполненность на открытии... если не поставили галку, считаем
  // что сразу всё ок") — pure UI affordance, not a wire field: unchecked
  // means "nothing to report at opening," so most racks (the common case)
  // never need the validator to touch a percentage/time field at all.
  // Derived from the data itself so a block parsed out of real report text
  // opens in the right state without a separate stored flag.
  const [openingHasIssue, setOpeningHasIssue] = useState(
    () => initial.openingPct !== 100 || Boolean(initial.filledAt) || initial.neverFilled,
  );

  // Auto-derive "Проблема на открытии" from filledAt vs the zone's real
  // opening time (deriveOpeningProblem, mirrors backend 1:1) whenever we
  // have both — no manual override in that case, see the checkbox fallback
  // below for when openTime isn't known. Skipped for neverFilled, which
  // hardcodes opening's is_problem=true regardless (build_category_tree).
  useEffect(() => {
    if (f.neverFilled) return;
    const derived = deriveOpeningProblem(f.filledAt, openTime);
    if (derived !== null && derived !== f.openingProblem) set({ openingProblem: derived });
  }, [f.filledAt, f.neverFilled, openTime]);

  // "Проблема на закрытии" (Alexey 2026-09-04): "причем тут проблема? Мы
  // просто ставим уровень заполненности на закрытии, и исходя из него уже
  // кружок считается сам" — no manual checkbox, closingProblem is always
  // derived from closingPct + rackType via deriveClosingSeverity (mirrors
  // backend closing_severity_of 1:1, see treeBlock.ts).
  useEffect(() => {
    const derived = deriveClosingSeverity(f.closingPct, f.rackType) > 0;
    if (derived !== f.closingProblem) set({ closingProblem: derived });
  }, [f.closingPct, f.rackType]);

  const canAutoDeriveOpeningProblem = openTime !== null;
  const previewRows = serializeCategoryTree(f);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {/* The DERIVED circle (first token of the preview header), not the
            parsed one — they differ on any block whose stored colour was
            hand-picked before the always-derived rule. */}
        {previewRows[0].split(' ')[0]} {f.rackType}
      </div>

      {/* Colour is derived from the parameters (Alexey 2026-09-08) — no picker. */}

      {/* Variant — ok (flat "ОК") / no_data (flat "нет измерений") / issues (branch tree) */}
      <div className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t('reportValidation.form.status')}</span>
        <div className="flex flex-wrap gap-2">
          {VARIANTS.map((v) => (
            <Button
              key={v}
              type="button"
              variant={f.variant === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => set({ variant: v })}
            >
              {t(`reportValidation.tree.variant_${v}`)}
            </Button>
          ))}
        </div>
      </div>

      {/* ОК with the opening/closing branches (2026-09-06 shape, Alexey:
          "надо оставлять пункты на открытии и к закрытию, не просто ОК"). */}
      {f.variant !== 'no_data' && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={f.details}
            onChange={(e) => set({ details: e.target.checked })}
            className="size-4"
          />
          <span className="text-muted-foreground">{t('reportValidation.tree.details')}</span>
        </label>
      )}

      {f.variant === 'issues' && (
        <>
          {/* Просадки */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t('reportValidation.form.shelfCount')}</span>
              <Input
                type="number"
                min={1}
                value={f.shelfCount}
                onChange={(e) => set({ shelfCount: Math.max(1, Number(e.target.value) || 1) })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t('reportValidation.form.duration')}</span>
              <Input
                type="number"
                min={0}
                step={0.1}
                value={f.problemHours}
                onChange={(e) => set({ problemHours: Math.max(0, Number(e.target.value) || 0) })}
              />
            </label>
            {/* 21.10 (Alexey): people in the queue zone during the dips; empty = no branch. */}
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t('reportValidation.form.dipVisitors')}</span>
              <Input
                type="number"
                min={0}
                value={f.dipVisitorsPm ?? ''}
                placeholder="—"
                onChange={(e) => set({ dipVisitorsPm: e.target.value === '' ? null : Math.max(0, Math.round(Number(e.target.value) || 0)) })}
              />
            </label>
          </div>
        </>
      )}

      {f.variant !== 'no_data' && f.details && (
        <>
          {/* Opening — gate checkbox collapses the whole subsection when
              there's nothing to report (Alexey 2026-09-04: "низкая
              заполненность на открытии... если не поставили галку, считаем
              что сразу всё ок"). When checked: opening % + a nested "выход
              на норму" block (time first, then "Не вышла на норму" which
              blocks/clears the time — that's the old top-level neverFilled
              checkbox, moved here and relabelled per his exact wording). */}
          <div className="flex flex-col gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={openingHasIssue}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setOpeningHasIssue(checked);
                  if (!checked) {
                    set({ openingPct: 100, filledAt: '', neverFilled: false, openingProblem: false });
                  }
                }}
                className="size-4"
              />
              <span className="text-muted-foreground">{t('reportValidation.tree.openingLowFill')}</span>
            </label>
            <span className="pl-6 text-xs text-muted-foreground/70">
              {t('reportValidation.tree.openingLowFillHint')}
            </span>

            {openingHasIssue && (
              <div className="flex flex-col gap-3 pl-6">
                <label className="flex flex-col gap-1">
                  <span className="text-muted-foreground">{t('reportValidation.tf.openPct')}</span>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={f.openingPct}
                    onChange={(e) => set({ openingPct: Number(e.target.value) })}
                    className="w-32"
                  />
                </label>

                <div className="flex flex-col gap-2 border-l-2 border-border pl-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-muted-foreground">{t('reportValidation.tf.normTime')}</span>
                    <Input
                      type="time"
                      disabled={f.neverFilled}
                      value={f.filledAt}
                      onChange={(e) => set({ filledAt: e.target.value })}
                      className="w-32 disabled:opacity-50"
                    />
                    {canAutoDeriveOpeningProblem && !f.neverFilled && (
                      <span className="text-xs text-muted-foreground/70">
                        {f.filledAt
                          ? f.openingProblem
                            ? t('reportValidation.tree.openingAutoProblem')
                            : t('reportValidation.tree.openingAutoOk')
                          : t('reportValidation.tree.openingAutoEmpty')}
                      </span>
                    )}
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={f.neverFilled}
                      onChange={(e) => {
                        const neverFilled = e.target.checked;
                        if (neverFilled) {
                          set({ neverFilled, filledAt: '', openingProblem: true });
                        } else {
                          // Mirrors the never_filled-forces-true guard this
                          // component already had to defend against once
                          // (see serializeCategoryTree's filledAt check) —
                          // don't leave a stale openingProblem=true with no
                          // real time behind it.
                          set({ neverFilled, openingProblem: f.filledAt ? f.openingProblem : false });
                        }
                      }}
                      className="size-4"
                    />
                    <span className="text-muted-foreground">{t('reportValidation.kp.neverFilled')}</span>
                  </label>
                  {!canAutoDeriveOpeningProblem && !f.neverFilled && (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={f.openingProblem}
                        onChange={(e) => set({ openingProblem: e.target.checked })}
                        className="size-4"
                      />
                      <span className="text-muted-foreground">
                        {t('reportValidation.kp.openingProblem')} ({t('reportValidation.tree.manualFallback')})
                      </span>
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Closing — "Проблема на закрытии" auto-derived from closingPct +
              rackType (deriveClosingSeverity, mirrors backend
              closing_severity_of 1:1 — Alexey 2026-09-04: "причем тут
              проблема? Мы просто ставим уровень заполненности... и исходя
              из него уже кружок считается сам"). Last-replenishment time
              shown automatically once there's a derived problem, no extra
              toggle — "чтобы была полная хронология". */}
          <div className="flex flex-col gap-2 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-muted-foreground">{t('reportValidation.tf.closePct')}</span>
              <Input
                type="number"
                min={0}
                max={100}
                value={f.closingPct}
                onChange={(e) => set({ closingPct: Number(e.target.value) })}
                className="w-32"
              />
              {f.neverFilled && f.closingPct <= 15 && (
                <span className="text-xs text-muted-foreground/70">
                  {t('reportValidation.kp.closeEmptyHint')}
                </span>
              )}
            </label>
            {!f.neverFilled && f.closingProblem && (
              <label className="flex flex-col gap-1">
                <span className="text-muted-foreground">{t('reportValidation.tree.replenishedAt')}</span>
                <Input
                  type="time"
                  value={f.replenishedAt}
                  onChange={(e) => set({ replenishedAt: e.target.value })}
                  className="w-32"
                />
              </label>
            )}
          </div>
        </>
      )}

      {/* Preview */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">{t('reportValidation.form.preview')}</span>
        <div className="report-body rounded-md border border-border bg-muted/40 p-2 text-sm">
          {previewRows.map((row, i) => (
            <div key={i} dangerouslySetInnerHTML={{ __html: row }} />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('reportValidation.form.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={() => onApply(previewRows)}>
          {t('reportValidation.form.apply')}
        </Button>
      </div>
    </div>
  );
}
